/*
 * TP Portal — scoped view for a Transportation Provider's own dispatchers.
 * Reuses the SAME data endpoints ASTAT uses, but scoped to the caller's tp_id via _require_tp_or_admin.
 * Renders the same trip board (via /scoped/trips) so behavior is identical.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import PhoneInput, { isValidE164 } from "../../components/PhoneInput";
import { confirm } from "../../components/ConfirmModal";
import ErrorBoundary from "../../components/ErrorBoundary";
import OnboardingTour from "../../components/OnboardingTour";
import SMSComplianceNotice from "../../components/SMSComplianceNotice";
import MedexOvaColdLoad from "../../components/MedexOvaColdLoad";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/tpt`;
const LS_KEY = "astat_tp_token";

const authHeaders = () => {
  const tok = localStorage.getItem(LS_KEY) || "";
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

function TPLoginForm({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCold, setShowCold] = useState(true);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/tp-auth/login`, { email, password });
      localStorage.setItem(LS_KEY, r.data.access_token);
      localStorage.setItem("astat_tp_user", JSON.stringify(r.data.user));
      onLoggedIn(r.data.user);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };
  return (
    <>
      {showCold && <MedexOvaColdLoad role="tp" minMs={1400} onDone={() => setShowCold(false)} />}
      <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#0B1220 0%,#142433 50%,#0B1220 100%)", display: "grid", placeItems: "center", padding: 24, color: "#fff" }} data-testid="tp-login-page">
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28, justifyContent: "center" }} data-testid="tp-login-brand">
          <img src="/logo.png" alt="MedexOVA" style={{ width: 46, height: 46, borderRadius: 12, objectFit: "contain" }} />
          <div>
            <div style={{ fontFamily: "ui-monospace,Menlo,monospace", letterSpacing: "0.05em", fontSize: 17, fontWeight: 800 }}>
              <span style={{ color: "#16A34A" }}>Medex</span><span style={{ color: "#C4B5FD" }}>OVA</span>
            </div>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#7FD8B7", fontWeight: 700 }}>Provider Portal</div>
          </div>
        </div>
        <form onSubmit={submit} style={{
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 16, padding: 24,
          backdropFilter: "blur(16px)", boxShadow: "0 30px 60px -20px rgba(0,0,0,0.5)",
        }}>
          <label style={{ display: "block", marginBottom: 14 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.16em", color: "#94A3B8", marginBottom: 6, fontWeight: 700 }}>Email</div>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="tp-login-email"
              style={{ width: "100%", height: 44, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 10, padding: "0 14px", fontSize: 14, color: "#fff", outline: "none" }}/>
          </label>
          <label style={{ display: "block", marginBottom: 18 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.16em", color: "#94A3B8", marginBottom: 6, fontWeight: 700 }}>Password</div>
            <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required data-testid="tp-login-password"
              style={{ width: "100%", height: 44, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 10, padding: "0 14px", fontSize: 14, color: "#fff", outline: "none" }}/>
          </label>
          <button disabled={busy} type="submit" data-testid="tp-login-submit"
            style={{ width: "100%", height: 48, borderRadius: 10, background: "#F59E0B", color: "#0B1220", fontWeight: 800, fontSize: 14, border: "none", cursor: "pointer", letterSpacing: "0.02em", boxShadow: "0 10px 30px -12px rgba(245,158,11,0.55)" }}>
            {busy ? "Signing in…" : "Sign in →"}
          </button>
        </form>
        <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 20, textAlign: "center" }}>
          Not registered? <a href="/partners/apply" style={{ color: "#F59E0B", fontWeight: 700 }}>Apply to become a provider</a>
        </div>
      </div>
      </div>
    </>
  );
}

const STATUS_COLORS = {
  ACCEPTED: "#E2E8F0", OFFERED_TP: "#DBEAFE", ASSIGNED: "#FEF3C7",
  EN_ROUTE: "#DCFCE7", ARRIVED_PU: "#EFE9FE", ON_TRIP: "#DBEAFE",
  ARRIVED_DO: "#EFE9FE", COMPLETED: "#BBF7D0", NO_SHOW_CONFIRMED: "#FECACA",
  REASSIGN_NEEDED: "#FECACA", CANCELLED: "#E2E8F0",
};

function TripCard({ trip, drivers, onAssign, onResend, onCopyUrl }) {
  const isNeedAssign = ["ACCEPTED", "OFFERED_TP", "REASSIGN_NEEDED"].includes(trip.status);
  return (
    <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, padding: 14 }} data-testid={`tp-trip-${trip.trip_id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div>
          <div style={{ fontWeight: 700, color: "#0F172A" }}>{trip.rider?.name}</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>{trip.rider?.mobility_type} · pickup {new Date(trip.schedule?.requested_pickup).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
        </div>
        <span style={{ background: STATUS_COLORS[trip.status] || "#F1F5F9", color: "#0F172A", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
          {trip.status}
        </span>
      </div>
      <div style={{ marginTop: 8, fontSize: 13, color: "#0F172A" }}>
        {(trip.pickup?.address || "").split(",")[0]} → {(trip.dropoff?.address || "").split(",")[0]}
      </div>
      {trip.assigned_driver && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#475569" }}>
          Driver: <b>{trip.assigned_driver.driver_name}</b> {trip.assigned_driver.driver_phone_masked}
        </div>
      )}
      {trip.dispatch_at && !trip.dispatch_sms_fired && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#B45309" }}>
          SMS scheduled for {new Date(trip.dispatch_at).toLocaleString([], { hour: "numeric", minute: "2-digit" })}
        </div>
      )}
      <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {isNeedAssign && (
          <select onChange={(e) => onAssign(trip, e.target.value)} value="" data-testid={`tp-assign-select-${trip.trip_id}`}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid #E2E0D8", borderRadius: 6 }}>
            <option value="">{trip.status === "REASSIGN_NEEDED" ? "Reassign to…" : "Assign to…"}</option>
            {drivers.map(d => <option key={d.id} value={d.id}>{d.name} · {d.phone}</option>)}
          </select>
        )}
        {!isNeedAssign && trip.assigned_driver && ["ASSIGNED","EN_ROUTE","ARRIVED_PU","ON_TRIP","ARRIVED_DO"].includes(trip.status) && (
          <>
            <button onClick={() => onCopyUrl(trip)} style={{ fontSize: 12, padding: "4px 8px", border: "1px solid #E2E0D8", borderRadius: 6, background: "#fff", cursor: "pointer" }}>Copy link</button>
            <button onClick={() => onResend(trip)} style={{ fontSize: 12, padding: "4px 8px", border: "1px solid #E2E0D8", borderRadius: 6, background: "#fff", cursor: "pointer" }}>Resend SMS</button>
            <button onClick={() => onAssign(trip, "__reassign__")} style={{ fontSize: 12, padding: "4px 8px", border: "1px solid #E2E0D8", borderRadius: 6, background: "#fff", cursor: "pointer" }} data-testid={`tp-reassign-${trip.trip_id}`}>Change driver</button>
          </>
        )}
      </div>
    </div>
  );
}

function TPDashboard({ user, onLogout }) {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [myTP, setMyTP] = useState(null);
  const [tab, setTab] = useState("live");
  const [changingDriver, setChangingDriver] = useState(null);

  const load = useCallback(async () => {
    try {
      const [tR, dR, tpR] = await Promise.all([
        axios.get(`${API_BASE}/scoped/trips`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/scoped/drivers`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/scoped/my-tp`, { headers: authHeaders() }),
      ]);
      setTrips(tR.data || []);
      setDrivers(dR.data || []);
      setMyTP(tpR.data || null);
    } catch (e) {
      if (e?.response?.status === 401) { onLogout(); return; }
      toast.error("Load failed");
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  const assign = async (trip, driver_id) => {
    if (!driver_id) return;
    if (driver_id === "__reassign__") { setChangingDriver(trip); return; }
    if (trip.assigned_driver && trip.assigned_driver.driver_id && trip.assigned_driver.driver_id !== driver_id) {
      const ok = await confirm({
        title: "Reassign this trip?",
        body: "The current driver's link will be revoked and a new SMS will fire to the new driver.",
        confirmLabel: "Reassign",
        tone: "danger",
      });
      if (!ok) return;
    }
    try {
      await axios.post(`${API_BASE}/trips/${trip.trip_id}/assign`, { tp_id: user.tp_id, driver_id }, { headers: authHeaders() });
      toast.success("Driver assigned · SMS handled by dispatch rules");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };
  const resend = async (trip) => {
    try {
      await axios.post(`${API_BASE}/trips/${trip.trip_id}/resend-sms`, {}, { headers: authHeaders() });
      toast.success("SMS resent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
  };
  const copyUrl = async (trip) => {
    try {
      const r = await axios.get(`${API_BASE}/trips/${trip.trip_id}/driver-url`, { headers: authHeaders() });
      await navigator.clipboard.writeText(r.data.driver_url);
      toast.success("Driver link copied");
    } catch (e) { toast.error("Copy failed"); }
  };

  const grouped = useMemo(() => {
    const g = { needs_action: [], live: [], completed: [] };
    for (const t of trips) {
      if (["ACCEPTED","OFFERED_TP","REASSIGN_NEEDED"].includes(t.status)) g.needs_action.push(t);
      else if (["COMPLETED","NO_SHOW_CONFIRMED","CANCELLED"].includes(t.status)) g.completed.push(t);
      else g.live.push(t);
    }
    return g;
  }, [trips]);

  const isPaidTier = myTP?.portal_tier?.tier === "paid";
  const enabledFeatures = myTP?.portal_tier?.enabled_features || [];
  const hasFeature = (key) => !key || (isPaidTier && enabledFeatures.includes(key));

  const TABS = [
    ["offers", "Offers", null],
    ["exceptions", "Exceptions", null],
    ["live", "Live trips", null],
    ["earnings", "Earnings", "earnings_invoices"],
    ["invoices", "Invoices", "earnings_invoices"],
    ["completed", "History", null],
    ["drivers", "Drivers", null],
    ["vehicles", "Vehicles", "vehicles"],
    ["payroll", "Payroll", "payroll"],
    ["reconciliation", "Reconciliation", "reconciliation"],
    ["insurance", "Insurance", "insurance_tracking"],
    ["kpi", "KPI Suite", "kpi_suite"],
    ["settings", "Settings", "settings_automation"],
    ["docs", "Documents", null],
  ];
  if (isPaidTier) TABS.push(["branding", "Branding", null]);

  const branding = myTP?.portal_branding;
  const hasBranding = isPaidTier && branding && (branding.logo_storage_path || branding.primary_color);
  const accentColor = hasBranding && branding.primary_color ? branding.primary_color : "#2B4C3B";

  return (
    <div style={{ minHeight: "100vh", background: "#F9F8F6" }}>
      <Toaster position="top-center" richColors />
      {user.impersonating && (
        <div style={{ background: "#B45309", color: "#fff", padding: "8px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, fontWeight: 700 }} data-testid="tp-impersonation-banner">
          <span>👁 Viewing as {myTP?.name || user.tp_name} — Admin impersonation session (expires in 30 min)</span>
          <button onClick={onLogout} data-testid="tp-impersonation-exit" style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Exit impersonation</button>
        </div>
      )}
      <div style={{ background: "#fff", borderBottom: "1px solid #E9EAE4", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {hasBranding && branding.logo_storage_path ? (
            <img src={`${API_BASE}/tpt/providers/${user.tp_id}/branding-logo`} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "contain", background: "#F1F5F9" }} data-testid="tp-branding-logo-img"/>
          ) : (
            <img src="/logo.png" alt="MedexOVA" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "contain" }} data-testid="tp-default-logo-img"/>
          )}
          <div>
            <div style={{ fontWeight: 700, color: "#0F172A", fontSize: 15 }}>{myTP?.name || user.tp_name || "Provider Portal"}</div>
            <div style={{ fontSize: 11, color: "#64748B" }}>{hasBranding && branding.welcome_message ? branding.welcome_message : user.email}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 800, padding: "3px 10px", borderRadius: 999, background: isPaidTier ? "#FEF3C7" : "#E2E8F0", color: isPaidTier ? "#B45309" : "#475569" }} data-testid="tp-portal-tier-badge">
            {isPaidTier ? "PAID" : "FREE"}
          </span>
          <button onClick={onLogout} style={{ fontSize: 12, color: "#64748B", background: "transparent", border: "none", cursor: "pointer" }}>Sign out</button>
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: 1300, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: "1px solid #E9EAE4", flexWrap: "wrap" }}>
          {TABS.map(([k, l, feature]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tp-tab-${k}`}
              style={{ background: "transparent", border: "none", cursor: "pointer", padding: "10px 4px", fontWeight: 600, fontSize: 14, color: tab === k ? accentColor : "#64748B", borderBottom: tab === k ? `2px solid ${accentColor}` : "2px solid transparent", display: "flex", alignItems: "center", gap: 4 }}>
              {l}
              {feature && !hasFeature(feature) && <span style={{ fontSize: 9, background: "#F59E0B", color: "#fff", borderRadius: 4, padding: "1px 4px", fontWeight: 800 }}>PAID</span>}
            </button>
          ))}
        </div>

        {tab === "live" && (
          <>
            {grouped.needs_action.length > 0 && (
              <>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#B45309", marginBottom: 8 }}>Needs your action</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12, marginBottom: 24 }}>
                  {grouped.needs_action.map(t => <TripCard key={t.trip_id} trip={t} drivers={drivers} onAssign={assign} onResend={resend} onCopyUrl={copyUrl}/>)}
                </div>
              </>
            )}
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", marginBottom: 8 }}>In progress</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
              {grouped.live.map(t => <TripCard key={t.trip_id} trip={t} drivers={drivers} onAssign={assign} onResend={resend} onCopyUrl={copyUrl}/>)}
              {grouped.live.length === 0 && <div style={{ fontSize: 13, color: "#64748B" }}>Nothing in progress.</div>}
            </div>
          </>
        )}

        {tab === "completed" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
            {grouped.completed.map(t => <TripCard key={t.trip_id} trip={t} drivers={drivers} onAssign={assign} onResend={resend} onCopyUrl={copyUrl}/>)}
            {grouped.completed.length === 0 && <div style={{ fontSize: 13, color: "#64748B" }}>No history yet.</div>}
          </div>
        )}

        {tab === "drivers" && (
          <TPDriversTab tpId={user.tp_id} drivers={drivers} onChanged={load}/>
        )}

        {tab === "earnings" && (
          hasFeature("earnings_invoices") ? <TPEarningsTab /> : <TPUpsellCard label="Earnings & Invoices Detail"/>
        )}

        {tab === "invoices" && (
          hasFeature("earnings_invoices") ? <TPInvoicesTab /> : <TPUpsellCard label="Earnings & Invoices Detail"/>
        )}

        {tab === "offers" && (
          <TPOffersTab tpId={user.tp_id} onAccepted={load} />
        )}

        {tab === "docs" && (
          <TPDocsTab tpId={user.tp_id} tpName={myTP?.name} />
        )}

        {tab === "settings" && (
          hasFeature("settings_automation") ? <TPSettingsTab /> : <TPUpsellCard label="Settings & Dispatch Automation"/>
        )}

        {tab === "exceptions" && (
          <TPExceptionsTab />
        )}

        {tab === "vehicles" && (
          hasFeature("vehicles") ? <TPVehiclesTab tpId={user.tp_id} /> : <TPUpsellCard label="Vehicle Management"/>
        )}

        {tab === "payroll" && (
          hasFeature("payroll") ? <TPPayrollTab /> : <TPUpsellCard label="Driver Payroll"/>
        )}

        {tab === "reconciliation" && (
          hasFeature("reconciliation") ? <TPReconciliationTab /> : <TPUpsellCard label="Broker/Facility Reconciliation"/>
        )}

        {tab === "insurance" && (
          hasFeature("insurance_tracking") ? <TPInsuranceTab /> : <TPUpsellCard label="Insurance Tracking"/>
        )}

        {tab === "kpi" && (
          hasFeature("kpi_suite") ? <TPKPITab /> : <TPUpsellCard label="Full KPI Suite"/>
        )}

        {tab === "branding" && (
          <TPBrandingTab tpId={user.tp_id} branding={branding} onSaved={load}/>
        )}

        <div style={{ marginTop: 40, padding: 16, background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", marginBottom: 8 }}>Contract rules</div>
          {myTP && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, fontSize: 13 }}>
              <div>Free wait: <b>{myTP.wait_time_minutes} min</b></div>
              <div>No-show wait: <b>{myTP.no_show_wait_minutes} min</b></div>
              <div>Dispatch lead: <b>{myTP.dispatch_lead_time_minutes || 60} min</b></div>
              <div>Offer TTL: <b>{myTP.offer_timeout_seconds}s</b></div>
              <div>Dead miles: <b>{myTP.pays_dead_miles ? `$${myTP.dead_mile_rate?.toFixed(2)}/mi` : "off"}</b></div>
              <div>Auto-assign: <b>{myTP.auto_assign_enabled ? myTP.auto_assign_strategy : "off"}</b></div>
            </div>
          )}
        </div>
      </div>

      {changingDriver && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 50, display: "grid", placeItems: "center", padding: 16 }} onClick={() => setChangingDriver(null)}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 20, width: "100%", maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Change driver</div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>This will kill the current driver&apos;s link and text the new driver.</div>
            <select onChange={(e) => { if (e.target.value) { assign(changingDriver, e.target.value); setChangingDriver(null); } }} defaultValue="" style={{ width: "100%", padding: "8px 12px", border: "1px solid #E2E0D8", borderRadius: 8 }} data-testid="tp-reassign-select">
              <option value="">Pick a driver…</option>
              {drivers.filter(d => d.id !== changingDriver?.assigned_driver?.driver_id).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button onClick={() => setChangingDriver(null)} style={{ fontSize: 13, background: "transparent", border: "none", color: "#64748B", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------- TP Earnings tab — Magic Moment #2 completion --------
 * "I know exactly what I earned and why."
 * One screen, one question. Big number → weekly rollup → per-trip drill-down
 * → invoice status. TP scoped via /api/tpt/scoped/earnings (server enforces).
 */
function TPEarningsTab() {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(30);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${API_BASE}/scoped/earnings`, { headers: authHeaders(), params: { days } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load earnings");
    } finally { setLoading(false); }
  }, [days]);
  React.useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const contract = data?.contract || {};
  const trips = data?.trips || [];
  const invoices = data?.invoices || [];

  const usd = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const invoiceTone = (st) => {
    switch ((st || "").toLowerCase()) {
      case "paid":       return { bg: "#DCFCE7", fg: "#166534" };
      case "issued":     return { bg: "#DBEAFE", fg: "#1E40AF" };
      case "sent":       return { bg: "#DBEAFE", fg: "#1E40AF" };
      case "uninvoiced": return { bg: "#FEF3C7", fg: "#B45309" };
      case "overdue":    return { bg: "#FECACA", fg: "#991B1B" };
      default:           return { bg: "#E2E8F0", fg: "#334155" };
    }
  };

  return (
    <div data-testid="tp-earnings-tab">
      {/* Hero: the big number */}
      <div style={{ background: "linear-gradient(135deg,#2B4C3B,#3A7BD5)", color: "#fff", padding: 24, borderRadius: 16, marginBottom: 20 }} data-testid="tp-earnings-hero">
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.18em", opacity: 0.8 }}>
          You earned · last {days} days
        </div>
        <div style={{ fontSize: 44, fontWeight: 800, marginTop: 6, letterSpacing: "-0.02em" }} data-testid="tp-earnings-hero-total">
          {loading ? "…" : usd(s.gross_usd)}
        </div>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
          across <b>{s.trip_count ?? 0}</b> completed trips
        </div>

        {/* Money split */}
        <div style={{ display: "flex", gap: 18, marginTop: 18, flexWrap: "wrap" }}>
          <SplitStat label="Paid" value={usd(s.paid_usd)} tone="ok" testid="tp-earnings-paid"/>
          <SplitStat label="Outstanding" value={usd(s.outstanding_usd)} tone={s.outstanding_usd > 0 ? "watch" : "muted"} testid="tp-earnings-outstanding"/>
          <SplitStat label="Uninvoiced" value={usd(s.uninvoiced_usd)} tone={s.uninvoiced_usd > 0 ? "watch" : "muted"} testid="tp-earnings-uninvoiced"/>
        </div>

        {/* Window picker */}
        <div style={{ display: "flex", gap: 6, marginTop: 18 }}>
          {[7, 30, 90, 365].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`tp-earnings-window-${d}`}
              style={{
                background: days === d ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.15)",
                color: days === d ? "#0F172A" : "#fff",
                border: "none", padding: "6px 14px", borderRadius: 999,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                transition: "all 200ms ease",
              }}>
              {d === 7 ? "7d" : d === 30 ? "30d" : d === 90 ? "90d" : "1y"}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ background: "#FECACA", color: "#991B1B", padding: 12, borderRadius: 10, marginBottom: 14 }} data-testid="tp-earnings-error">
          {error}
        </div>
      )}

      {/* Why — contract rules explaining the math */}
      <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, padding: 14, marginBottom: 20 }} data-testid="tp-earnings-contract">
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", marginBottom: 6 }}>
          How your earnings are calculated
        </div>
        <div style={{ fontSize: 13, color: "#0F172A" }}>
          <b>{usd(contract.base_fare)}</b> base fare + <b>{usd(contract.per_mile_rate)}/mile</b> actual
          {contract.pays_dead_miles && <> + <b>{usd(contract.dead_mile_rate)}/mile</b> dead miles</>}
          . Per your MedexOVA provider contract. Contact ops if these numbers are wrong.
        </div>
      </div>

      {/* Invoices */}
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", marginBottom: 8 }}>
        Invoices MedexOVA sent you ({invoices.length})
      </div>
      <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, overflow: "hidden", marginBottom: 24 }}>
        {invoices.length === 0 && <div style={{ padding: 16, color: "#64748B", fontSize: 13 }} data-testid="tp-earnings-invoices-empty">
          No invoices issued for this window yet.
        </div>}
        {invoices.map((inv) => {
          const tone = invoiceTone(inv.status);
          return (
            <div key={inv.invoice_id} style={{ padding: 12, borderBottom: "1px solid #E9EAE4", display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid={`tp-earnings-invoice-${inv.invoice_id}`}>
              <div>
                <div style={{ fontWeight: 600, color: "#0F172A" }}>{usd(inv.total)}</div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                  {(inv.period_start || "").slice(0, 10)} → {(inv.period_end || "").slice(0, 10)}
                </div>
              </div>
              <span style={{ background: tone.bg, color: tone.fg, padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                {inv.status || "—"}
              </span>
            </div>
          );
        })}
      </div>

      {/* Trip drill-down */}
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", marginBottom: 8 }}>
        Completed trips ({trips.length})
      </div>
      <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, overflow: "hidden" }}>
        {trips.length === 0 && (
          <div style={{ padding: 20, color: "#64748B", fontSize: 13, textAlign: "center" }} data-testid="tp-earnings-trips-empty">
            No completed trips in this window yet. When your drivers complete trips assigned to your fleet, they&apos;ll appear here with the exact fare math.
          </div>
        )}
        {trips.map((t) => {
          const tone = invoiceTone(t.invoice_status);
          return (
            <div key={t.trip_id} style={{ padding: 12, borderBottom: "1px solid #E9EAE4" }} data-testid={`tp-earnings-trip-${t.trip_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: "#0F172A" }}>
                    {t.rider_name || "—"} · {t.driver_name || "unassigned"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {(t.pickup || "").split(",")[0]} → {(t.dropoff || "").split(",")[0]}
                  </div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>
                    {t.actual_miles?.toFixed(1)} mi{t.dead_miles > 0 && ` · ${t.dead_miles.toFixed(1)} dead`} · {t.completed_at ? new Date(t.completed_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, color: "#0F172A" }}>{usd(t.total_usd)}</div>
                  <span style={{ background: tone.bg, color: tone.fg, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", display: "inline-block", marginTop: 4 }}>
                    {t.invoice_status || "—"}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SplitStat({ label, value, tone, testid }) {
  const toneStyles = {
    ok:    { bg: "rgba(220,252,231,0.9)", fg: "#166534" },
    watch: { bg: "rgba(254,243,199,0.9)", fg: "#7C4A03" },
    muted: { bg: "rgba(255,255,255,0.15)", fg: "#fff" },
  }[tone] || { bg: "rgba(255,255,255,0.15)", fg: "#fff" };
  return (
    <div data-testid={testid} style={{ background: toneStyles.bg, color: toneStyles.fg, padding: "10px 14px", borderRadius: 10, minWidth: 130 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", opacity: 0.8 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}


function TPDriversTab({ tpId, drivers, onChanged }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", vehicle_type: "wheelchair", license_state: "" });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!form.name || !form.phone) { toast.error("Name + phone required"); return; }
    if (!isValidE164(form.phone)) { toast.error("Phone must be E.164 (+1XXXXXXXXXX)"); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/providers/${tpId}/drivers`, form, { headers: authHeaders() });
      toast.success("Driver added — pending MedexOVA credentialing approval");
      setForm({ name: "", phone: "", email: "", vehicle_type: "wheelchair", license_state: "" });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  const copyBeacon = async (d) => {
    try {
      const r = await axios.post(`${API_BASE}/drivers/${d.id}/beacon-key`, {}, { headers: authHeaders() });
      await navigator.clipboard.writeText(r.data.beacon_url);
      toast.success(`Beacon URL copied — send to ${d.name.split(" ")[0]}`);
    } catch (e) { toast.error("Beacon failed"); }
  };
  const smsBeacon = async (d) => {
    if (!window.confirm(`Text the beacon URL to ${d.name}?`)) return;
    try {
      const r = await axios.post(`${API_BASE}/drivers/${d.id}/beacon-sms`, {}, { headers: authHeaders() });
      toast.success(`Beacon SMS queued to ${r.data.queued_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "SMS failed"); }
  };
  const resolveFlag = async (d) => {
    try {
      await axios.post(`${API_BASE}/drivers/${d.id}/flag/resolve`, {}, { headers: authHeaders() });
      toast.success("Marked resolved");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't resolve"); }
  };
  const credChip = (s) => {
    const map = { cleared: ["#DCFCE7", "#166534"], pending: ["#FEF3C7", "#B45309"], in_review: ["#DBEAFE", "#1E40AF"], rejected: ["#FECACA", "#991B1B"], lapsed: ["#FECACA", "#991B1B"] };
    const [bg, fg] = map[s] || ["#E2E8F0", "#334155"];
    return <span style={{ background: bg, color: fg, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>{s === "lapsed" ? "suspended" : (s || "—")}</span>;
  };
  const expiryNote = (d) => d.credentialing_expiring_field
    ? `${(d.credentialing_expiring_field || "").replace(/_/g, " ")} expires ${d.credentialing_expires_on || "soon"}`
    : null;
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", marginBottom: 8 }}>My drivers</div>
      {drivers.length > 0 && (
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, fontSize: 12, color: "#166534" }}>
          <b>Credentials on file</b> — your onboarding documents and driver records were submitted by MedexOVA on your behalf.
          If a driver shows <b>pending</b>, we're reviewing — no action needed unless your VA reaches out.
          Use the form below only to add <b>new drivers</b> not yet in the system.
        </div>
      )}
      <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
        {drivers.length === 0 && <div style={{ padding: 16, color: "#64748B", fontSize: 13 }}>No drivers yet.</div>}
        {drivers.map(d => {
          const reasons = Array.isArray(d.medexova_rejection_reasons) ? d.medexova_rejection_reasons : [];
          return (
          <div key={d.id} style={{ padding: 12, borderBottom: "1px solid #E9EAE4" }} data-testid={`tp-driver-${d.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{d.name} <span style={{ marginLeft: 6 }}>{credChip(d.medexova_credentialing_status)}</span></div>
                <div style={{ fontSize: 12, color: "#64748B" }}>{d.phone} · {d.vehicle_type} · <span style={{ textTransform: "capitalize" }}>{d.shift_status || "offline"}</span></div>
                {expiryNote(d) && <div style={{ fontSize: 11, color: "#B45309", marginTop: 4 }} data-testid={`tp-driver-expiry-${d.id}`}>⏳ {expiryNote(d)}</div>}
                {d.last_notice_reply?.action === "flagged" && !d.last_notice_reply?.resolved_at && (
                  <div style={{ fontSize: 11, color: "#9A3412", marginTop: 4, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }} data-testid={`tp-driver-flag-${d.id}`}>
                    <span>🚩 Flagged: &ldquo;{d.last_notice_reply.message || "no details"}&rdquo;</span>
                    <button onClick={() => resolveFlag(d)} data-testid={`tp-driver-flag-resolve-${d.id}`}
                      style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, border: "1px solid #9A3412", background: "#fff", color: "#9A3412", cursor: "pointer" }}>
                      Mark resolved
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => copyBeacon(d)} style={{ fontSize: 12, padding: "6px 10px", border: "1px solid #E2E0D8", borderRadius: 6, background: "#fff", cursor: "pointer", marginRight: 6 }}>Copy Beacon</button>
              <button onClick={() => smsBeacon(d)} style={{ fontSize: 12, padding: "6px 10px", border: "none", borderRadius: 6, background: "#3A7BD5", color: "#fff", cursor: "pointer" }} data-testid={`tp-beacon-sms-${d.id}`}>SMS Beacon</button>
            </div>
            {reasons.length > 0 && (
              <div style={{ marginTop: 8, padding: 8, background: "#FEF3C7", border: "1px solid #F5D48C", borderRadius: 6, fontSize: 12, color: "#7C4A03" }} data-testid={`tp-driver-rejection-${d.id}`}>
                <b>Not cleared:</b> {reasons.join(" · ")}
              </div>
            )}
          </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", marginBottom: 8 }}>Add driver</div>
      <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>New drivers land as <b>pending</b> — MedexOVA must approve credentialing before they can receive trips.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <input placeholder="Full name" value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} data-testid="tp-add-driver-name"
            style={{ height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}/>
          <PhoneInput value={form.phone} onChange={(v) => setForm({...form, phone:v})} testid="tp-add-driver-phone" required />
          <input placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({...form, email:e.target.value})}
            style={{ height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}/>
          <select value={form.vehicle_type} onChange={(e) => setForm({...form, vehicle_type:e.target.value})}
            style={{ height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}>
            <option value="sedan">Sedan</option><option value="wheelchair">Wheelchair</option>
            <option value="stretcher">Stretcher</option><option value="van">Van</option>
          </select>
        </div>
        <div style={{ marginTop: 10 }}><SMSComplianceNotice compact /></div>
        <div style={{ marginTop: 12, textAlign: "right" }}>
          <button onClick={add} disabled={busy} data-testid="tp-add-driver-submit"
            style={{ background: "#2B4C3B", color: "#fff", padding: "10px 18px", borderRadius: 8, fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
            {busy ? "Adding…" : "Add driver"}
          </button>
        </div>
      </div>
      <TPBulkImport tpId={tpId} onDone={onChanged} />
    </div>
  );
}

/* -------- Bulk driver CSV import (Wave 5 / Stream K) --------
 * Paste rows from Excel/Google Sheets — one driver per line. Header row
 * required. See backend /providers/{tp_id}/drivers/bulk-import.
 */
function TPBulkImport({ tpId, onDone }) {
  const [csv, setCsv] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const columns = "name,phone,email,vehicle_type,vehicle_make,vehicle_model,vehicle_year,vehicle_vin,assigned_vehicle_plate";
  const sample = `${columns}\nJane Smith,+15551110022,jane@example.com,sedan,Toyota,Camry,2021,1HGCM82633A004352,ABC1234\nMike Jones,+15551110033,,wheelchair,Ford,Transit,2019,1FTBW2CM0JKA00001,XYZ7890`;
  const downloadTemplate = () => {
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "driver-import-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const submit = async () => {
    if (!csv.trim()) { toast.error("Paste CSV rows first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(`${API_BASE}/providers/${tpId}/drivers/bulk-import`, { csv }, { headers: authHeaders() });
      setResult(r.data);
      toast.success(`${r.data.created} added · ${r.data.skipped_duplicates} duplicates skipped · ${r.data.errors} errors`);
      if (r.data.created > 0) onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", marginBottom: 8 }}>Bulk import drivers (CSV)</div>
      <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>
          Paste a spreadsheet. First row must be a header. Columns: <b>name</b>, <b>phone</b>, <b>email</b>, <b>vehicle_type</b>, <b>vehicle_make</b>, <b>vehicle_model</b>, <b>vehicle_year</b>, <b>vehicle_vin</b>, <b>assigned_vehicle_plate</b> (case-insensitive). Vehicle fields are needed for MedexOva credentialing to go through. Duplicates are skipped, not overwritten.
        </div>
        <textarea value={csv} onChange={e => setCsv(e.target.value)} rows={8}
          data-testid="tp-bulk-csv"
          placeholder={sample}
          style={{ width: "100%", fontFamily: "monospace", fontSize: 12, padding: 10, border: "1px solid #E2E0D8", borderRadius: 8, background: "#F8FAFC" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setCsv(sample)} type="button"
              style={{ fontSize: 12, padding: "6px 10px", border: "1px solid #E2E0D8", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
              Load sample
            </button>
            <button onClick={downloadTemplate} type="button" data-testid="tp-bulk-download-template"
              style={{ fontSize: 12, padding: "6px 10px", border: "1px solid #E2E0D8", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
              Download CSV template
            </button>
          </div>
          <button onClick={submit} disabled={busy} data-testid="tp-bulk-submit"
            style={{ background: "#2B4C3B", color: "#fff", padding: "10px 18px", borderRadius: 8, fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
            {busy ? "Importing…" : "Import drivers"}
          </button>
        </div>
        {result && (
          <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "#F5F3EA", fontSize: 12 }}>
            <div><b>Created:</b> {result.created} · <b>Duplicates skipped:</b> {result.skipped_duplicates} · <b>Errors:</b> {result.errors}</div>
            {result.results?.slice(0, 20).map((r, i) => (
              <div key={i} style={{ marginTop: 4, color: r.status === "ok" ? "#166534" : r.status === "skipped" ? "#B45309" : "#B91C1C" }}>
                Row {r.row}: {r.status === "ok" ? `✓ ${r.name}` : r.status === "skipped" ? `⚠ ${r.reason || "duplicate"}` : `✗ ${r.reason}`}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TPPortal() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("astat_tp_user") || "null"); } catch { return null; }
  });
  const [checkingImpersonation, setCheckingImpersonation] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get("impersonate");
    const inviteToken = params.get("invite");
    const magicToken = impersonateToken || inviteToken;
    const isInvite = !!inviteToken && !impersonateToken;

    if (!magicToken) { setCheckingImpersonation(false); return; }
    (async () => {
      try {
        localStorage.setItem(LS_KEY, magicToken);
        const meR = await axios.get(`${API_BASE}/tp-auth/me`, { headers: { Authorization: `Bearer ${magicToken}` } });
        const tpR = await axios.get(`${API_BASE}/scoped/my-tp`, { headers: { Authorization: `Bearer ${magicToken}` } });
        const loggedInUser = {
          tp_id: meR.data.tp_id, email: meR.data.email, name: meR.data.name,
          tp_name: tpR.data?.name,
          // invite links are for the TP themselves — no admin-viewing banner
          impersonating: !isInvite,
        };
        localStorage.setItem("astat_tp_user", JSON.stringify(loggedInUser));
        setUser(loggedInUser);
        window.history.replaceState({}, "", window.location.pathname);
      } catch (e) {
        toast.error("Portal link expired or invalid — please log in");
        localStorage.removeItem(LS_KEY);
      } finally { setCheckingImpersonation(false); }
    })();
  }, []);

  const logout = () => {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem("astat_tp_user");
    setUser(null);
  };
  if (checkingImpersonation) return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: "#64748B" }}>Loading…</div>;
  if (!user) return <><Toaster position="top-center" richColors/><TPLoginForm onLoggedIn={setUser}/></>;
  return (
    <ErrorBoundary name="provider portal">
      <TPDashboard user={user} onLogout={logout}/>
      <OnboardingTour />
    </ErrorBoundary>
  );
}

// ─────────────────────────────────────────────────────────
// TP Invoices tab · Schedule A ledger + invoices + dispute
// Closes the settlement loop from the TP side (Design Language v2 §E4).
// ─────────────────────────────────────────────────────────
function TPInvoicesTab() {
  const [ledger, setLedger] = React.useState({ items: [], total_unpaid_cents: 0 });
  const [invoices, setInvoices] = React.useState([]);
  const [schedule, setSchedule] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [disputeRow, setDisputeRow] = React.useState(null);
  const [reason, setReason] = React.useState("");
  const [note, setNote] = React.useState("");
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("astat_tp_token")}` });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [lR, iR, sR] = await Promise.all([
        axios.get(`${API_BASE}/scoped/settlement/ledger`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/scoped/settlement/invoices`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/scoped/settlement/schedule`, { headers: authHeaders() }),
      ]);
      setLedger(lR.data?.data || { items: [], total_unpaid_cents: 0 });
      setInvoices(iR.data?.data?.items || []);
      setSchedule(sR.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load invoices");
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const submitDispute = async () => {
    if (!disputeRow || !reason.trim()) return;
    try {
      await axios.post(`${API_BASE}/scoped/settlement/dispute`,
        { ledger_id: disputeRow.id, reason, note },
        { headers: authHeaders() });
      toast.success("Dispute submitted · MedexOVA ops will review");
      setDisputeRow(null); setReason(""); setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispute failed");
    }
  };

  const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div data-testid="tp-invoices-tab" style={{ display: "grid", gap: 20 }}>
      {/* Schedule A summary + unpaid total */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
        <div style={{ padding: 16, borderRadius: 12, background: "linear-gradient(135deg,#0B1220 0%,#142433 100%)", color: "#E2E8F0", border: "1px solid rgba(245,158,11,0.28)" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "#94A3B8", fontWeight: 800 }}>Unpaid balance</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#F59E0B", marginTop: 4 }} data-testid="tp-invoices-unpaid">{fmt(ledger.total_unpaid_cents)}</div>
          <div style={{ fontSize: 11, color: "#94A3B8" }}>{ledger.items.length} unpaid line{ledger.items.length === 1 ? "" : "s"}</div>
        </div>
        {schedule && (
          <div style={{ padding: 16, borderRadius: 12, background: "#fff", border: "1px solid #E9EAE4" }}>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#64748B", fontWeight: 800, marginBottom: 6 }}>Schedule A</div>
            <div style={{ fontSize: 12, color: "#0F172A", lineHeight: 1.6 }}>
              Load: <b>{fmt(schedule.load_rate_cents)}</b> · Miles: <b>{fmt(schedule.mileage_rate_cents_per_mi)}/mi</b><br/>
              Deadhead: <b>{fmt(schedule.deadhead_rate_cents_per_mi)}/mi</b> · Wait: <b>{fmt(schedule.wait_rate_cents_per_min)}/min</b> (first {schedule.wait_free_minutes} min free)<br/>
              Roundtrip multiplier: <b>{schedule.roundtrip_multiplier}×</b>
              {schedule.is_default && <span style={{ marginLeft: 8, fontSize: 10, color: "#F59E0B", fontWeight: 800 }}>· DEFAULT</span>}
            </div>
          </div>
        )}
      </div>

      {/* Unpaid ledger */}
      <div>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#0F172A", marginBottom: 8, fontWeight: 800 }} data-testid="tp-invoices-ledger-header">Unpaid ledger</div>
        {loading && <div style={{ fontSize: 13, color: "#64748B" }}>Loading…</div>}
        {!loading && ledger.items.length === 0 && (
          <div style={{ padding: 24, background: "#fff", border: "1px dashed #E9EAE4", borderRadius: 12, textAlign: "center", color: "#64748B", fontSize: 13 }}>No unpaid ledger rows.</div>
        )}
        {ledger.items.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, overflow: "hidden" }} data-testid="tp-invoices-ledger">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#F5F3EA", textAlign: "left" }}>
                  <th style={{ padding: "10px 14px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B" }}>Completed</th>
                  <th style={{ padding: "10px 14px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B" }}>Trip</th>
                  <th style={{ padding: "10px 14px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B" }}>Driver</th>
                  <th style={{ padding: "10px 14px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B" }}>Breakdown</th>
                  <th style={{ padding: "10px 14px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", textAlign: "right" }}>Total</th>
                  <th style={{ padding: "10px 14px" }}></th>
                </tr>
              </thead>
              <tbody>
                {ledger.items.map(row => {
                  const b = row.breakdown || {};
                  const trip = String(row.trip_id || "");
                  const driver = row.driver_name || String(row.driver_id || "").slice(0, 8);
                  const completed = String(row.completed_at || "");
                  return (
                    <tr key={row.id} style={{ borderTop: "1px solid #E9EAE4" }}>
                      <td style={{ padding: "10px 14px", fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11, color: "#64748B" }}>{completed.slice(0, 16).replace("T", " ")}</td>
                      <td style={{ padding: "10px 14px", fontWeight: 700 }}>{trip.slice(0, 12)}</td>
                      <td style={{ padding: "10px 14px" }}>{driver}</td>
                      <td style={{ padding: "10px 14px", fontSize: 11, color: "#64748B" }}>
                        Load {fmt(b.load_cents)} · Miles {(b.miles || 0).toFixed(1)}mi {fmt(b.miles_cents)} · DH {(b.deadhead_miles || 0).toFixed(1)}mi {fmt(b.deadhead_cents)} · Wait {fmt(b.wait_cents)}
                        {b.is_roundtrip && <span style={{ marginLeft: 6, fontSize: 10, color: "#F59E0B", fontWeight: 800 }}>· RT ×{b.roundtrip_multiplier}</span>}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 800, color: "#0F172A" }}>{fmt(row.total_cents)}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <button onClick={() => setDisputeRow(row)} data-testid={`tp-dispute-btn-${trip.slice(0,8)}`}
                          style={{ background: "transparent", border: "1px solid #E9EAE4", padding: "4px 10px", fontSize: 11, fontWeight: 700, color: "#B45309", borderRadius: 6, cursor: "pointer" }}>
                          Dispute
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice history */}
      <div>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#0F172A", marginBottom: 8, fontWeight: 800 }}>Invoice history</div>
        {invoices.length === 0 && (
          <div style={{ padding: 24, background: "#fff", border: "1px dashed #E9EAE4", borderRadius: 12, textAlign: "center", color: "#64748B", fontSize: 13 }}>No invoices issued yet.</div>
        )}
        {invoices.length > 0 && (
          <div style={{ display: "grid", gap: 8 }} data-testid="tp-invoices-history">
            {invoices.map(inv => (
              <div key={inv.id} style={{ padding: 14, background: "#fff", border: "1px solid #E9EAE4", borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{String(inv.id || "").slice(0, 12)}</div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>
                    {inv.period_start || "—"} → {inv.period_end || "—"} · {inv.trip_count} trip{inv.trip_count === 1 ? "" : "s"}
                    {inv.paid_ref && <> · paid ref <b>{inv.paid_ref}</b></>}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{
                    fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 800,
                    padding: "3px 8px", borderRadius: 6,
                    background: inv.status === "paid" ? "#D1FAE5" : inv.status === "voided" ? "#FEE2E2" : "#FEF3C7",
                    color: inv.status === "paid" ? "#047857" : inv.status === "voided" ? "#B91C1C" : "#B45309",
                  }}>{inv.status}</span>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#0F172A", minWidth: 92, textAlign: "right" }}>{fmt(inv.total_cents)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dispute modal */}
      {disputeRow && (
        <div onClick={() => setDisputeRow(null)} data-testid="tp-dispute-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(11,18,32,0.55)", zIndex: 60, display: "grid", placeItems: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 20, width: "min(460px, 92vw)", boxShadow: "0 30px 60px -20px rgba(0,0,0,0.4)" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B45309", fontWeight: 800, marginBottom: 6 }}>Dispute this line</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>{String(disputeRow.trip_id || "").slice(0, 12)} · {fmt(disputeRow.total_cents)}</div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>Explain what looks wrong. MedexOVA ops will review and reply.</div>
            <label style={{ display: "block", marginBottom: 10 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4, fontWeight: 700 }}>Reason</div>
              <select value={reason} onChange={e => setReason(e.target.value)} data-testid="tp-dispute-reason"
                style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 10px", fontSize: 14, background: "#fff" }}>
                <option value="">Choose a reason…</option>
                <option value="miles_wrong">Miles look wrong</option>
                <option value="deadhead_wrong">Deadhead not honored</option>
                <option value="wait_wrong">Wait time missing</option>
                <option value="roundtrip_missed">Roundtrip multiplier missed</option>
                <option value="not_our_trip">Not our trip</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label style={{ display: "block", marginBottom: 14 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4, fontWeight: 700 }}>Note (optional)</div>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} data-testid="tp-dispute-note"
                style={{ width: "100%", border: "1px solid #E2E0D8", borderRadius: 8, padding: 10, fontSize: 13, resize: "vertical" }}/>
            </label>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDisputeRow(null)} style={{ background: "transparent", border: "1px solid #E2E0D8", padding: "10px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <button onClick={submitDispute} disabled={!reason.trim()} data-testid="tp-dispute-submit"
                style={{ background: "#B45309", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: reason.trim() ? 1 : 0.5 }}>
                Submit dispute →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * TPOffersTab — Track 1 · Phase B UI wiring
 * ─────────────────────────────────────────
 * Renders every trip where the caller's tp_id sits in `offer_pool`, grouped by
 * pool status: PENDING (act now) · ACCEPTED · DECLINED · EXPIRED. Accept fires
 * the atomic first-accept-wins endpoint; decline is soft (trip stays offerable
 * to other TPs).  Zero duplicated business logic — all writes route through
 * the endpoints shipped in this batch.
 */
function TPOffersTab({ tpId, onAccepted }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/scoped/offers`, { headers: authHeaders() });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load offers");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const groups = React.useMemo(() => {
    const g = { pending: [], accepted: [], declined: [], expired: [] };
    for (const r of rows) {
      const e = (r.offer_pool || []).find(p => p.tp_id === tpId);
      if (!e) continue;
      if (e.status === "pending" && r.status === "OFFERED") g.pending.push(r);
      else if (e.status === "accepted") g.accepted.push(r);
      else if (e.status === "declined") g.declined.push(r);
      else g.expired.push(r);
    }
    return g;
  }, [rows, tpId]);

  const act = async (tripId, kind) => {
    setBusyId(tripId);
    try {
      await axios.post(`${API_BASE}/scoped/offers/${tripId}/${kind}`, {}, { headers: authHeaders() });
      toast.success(kind === "accept" ? "Offer accepted — assign a driver next" : "Offer declined");
      if (kind === "accept" && onAccepted) onAccepted();
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || `Could not ${kind}`;
      toast.error(msg);
    } finally {
      setBusyId("");
    }
  };

  if (loading) return <div style={{ padding: 24, color: "#64748B", fontSize: 13 }}>Loading offers…</div>;

  return (
    <div data-testid="tp-offers-tab">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700 }}>Trip offers</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0F1F17" }}>
            {groups.pending.length} pending
          </div>
        </div>
        <button onClick={load} data-testid="tp-offers-refresh"
          style={{ background: "transparent", border: "1px solid #E2E0D8", padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", color: "#64748B" }}>
          Refresh
        </button>
      </div>

      <OffersGroup label="Pending — first accept wins" tone="urgent" trips={groups.pending} tpId={tpId}
        onAccept={(id) => act(id, "accept")} onDecline={(id) => act(id, "decline")} busyId={busyId} />

      <OffersGroup label="Accepted (assign a driver next)" tone="ok" trips={groups.accepted} tpId={tpId}/>
      <OffersGroup label="Declined" tone="muted" trips={groups.declined} tpId={tpId}/>
      <OffersGroup label="Expired" tone="muted" trips={groups.expired} tpId={tpId}/>

      {rows.length === 0 && (
        <div style={{ padding: 32, textAlign: "center", background: "#F5F3EA", border: "1px dashed #E2E0D8", borderRadius: 10, color: "#64748B", fontSize: 13 }}>
          No offers yet. When MedexOVA routes a trip to your company, it will show up here first.
        </div>
      )}
    </div>
  );
}

function OffersGroup({ label, tone, trips, onAccept, onDecline, busyId, tpId }) {
  if (!trips || trips.length === 0) return null;
  const tint = tone === "urgent" ? "#B45309" : tone === "ok" ? "#2B4C3B" : "#64748B";
  const showActions = tone === "urgent" && onAccept && onDecline;
  return (
    <>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: tint, marginBottom: 8, marginTop: 18, fontWeight: 700 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 12 }}>
        {trips.map(t => {
          const e = (t.offer_pool || []).find(p => p.tp_id === tpId) || {};
          return (
            <div key={t.trip_id} style={{ background: "#fff", border: "1px solid #E2E0D8", borderRadius: 10, padding: 14 }} data-testid={`tp-offer-card-${t.trip_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontFamily: "ui-monospace,Menlo,monospace", color: "#64748B" }}>{(t.trip_id || "").slice(0, 8).toUpperCase()}</div>
                {e.expires_at && e.status === "pending" && <ExpiresIn iso={e.expires_at}/>}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1F17" }}>{t.rider?.name || "Rider"}</div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                {t.pickup?.address || "Pickup"} → {t.dropoff?.address || "Dropoff"}
              </div>
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                Requested pickup · {t.schedule?.requested_pickup ? new Date(t.schedule.requested_pickup).toLocaleString() : "—"}
              </div>
              {showActions && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button onClick={() => onAccept(t.trip_id)} disabled={busyId === t.trip_id}
                    data-testid={`tp-offer-accept-${t.trip_id}`}
                    style={{ background: "#2B4C3B", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 800, cursor: "pointer", opacity: busyId === t.trip_id ? 0.6 : 1 }}>
                    Accept
                  </button>
                  <button onClick={() => onDecline(t.trip_id)} disabled={busyId === t.trip_id}
                    data-testid={`tp-offer-decline-${t.trip_id}`}
                    style={{ background: "transparent", color: "#B45309", border: "1px solid #F5D48C", padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 800, cursor: "pointer", opacity: busyId === t.trip_id ? 0.6 : 1 }}>
                    Decline
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function ExpiresIn({ iso }) {
  const [rem, setRem] = React.useState("");
  React.useEffect(() => {
    const tick = () => {
      const ms = new Date(iso).getTime() - Date.now();
      if (ms <= 0) return setRem("expired");
      const mm = Math.floor(ms / 60000);
      const ss = Math.floor((ms % 60000) / 1000);
      setRem(`${mm}m ${ss.toString().padStart(2, "0")}s`);
    };
    tick(); const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, [iso]);
  return (
    <span style={{ fontSize: 11, color: "#B45309", fontWeight: 800, letterSpacing: "0.06em" }} data-testid="tp-offer-expires">
      ⏱ {rem}
    </span>
  );
}


/**
 * TPDocsTab — Track 3 · Phase M documentation center
 * ────────────────────────────────────────────────────
 * Handbook, MSA, Privacy Policy, Use Agreement, Autonomous Dispatch explainer,
 * Rule Package library, FAQ. First-drafts written by main agent; ASTAT legal
 * team will redline/replace later. Every document renders inline in the same
 * cinematic surface as the rest of the portal — no PDF downloads required.
 */
function TPDocsTab({ tpId, tpName }) {
  const [doc, setDoc] = React.useState("handbook");
  const docs = [
    ["handbook",           "Getting Started · Handbook"],
    ["autonomous",         "Autonomous Dispatch · How It Works"],
    ["msa",                "Master Service Agreement"],
    ["privacy",            "Privacy Policy"],
    ["use",                "Acceptable Use Agreement"],
    ["credentialing",      "Credentialing & MedexOva"],
    ["rules",              "Rule Package Library"],
    ["faq",                "FAQ · Troubleshooting"],
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 24 }} data-testid="tp-docs-tab">
      <nav style={{ borderRight: "1px solid #E9EAE4", paddingRight: 12 }}>
        {docs.map(([k, l]) => (
          <button key={k} onClick={() => setDoc(k)} data-testid={`tp-doc-nav-${k}`}
            style={{ display: "block", width: "100%", textAlign: "left", background: doc === k ? "#F5F3EA" : "transparent", border: "none", padding: "10px 12px", borderRadius: 6, marginBottom: 4, fontSize: 13, color: doc === k ? "#0F1F17" : "#64748B", fontWeight: doc === k ? 800 : 600, cursor: "pointer" }}>
            {l}
          </button>
        ))}
      </nav>
      <article style={{ maxWidth: 780 }}>
        <TPDocContent slug={doc} tpName={tpName}/>
      </article>
    </div>
  );
}

const DocH1 = ({ children }) => <h1 style={{ fontSize: 30, fontWeight: 900, color: "#0F1F17", letterSpacing: "-0.02em", marginBottom: 6 }}>{children}</h1>;
const DocH2 = ({ children }) => <h2 style={{ fontSize: 17, fontWeight: 800, color: "#0F1F17", marginTop: 24, marginBottom: 8 }}>{children}</h2>;
const DocP  = ({ children }) => <p style={{ fontSize: 14, lineHeight: 1.65, color: "#334155", marginBottom: 10 }}>{children}</p>;
const DocLI = ({ children }) => <li style={{ fontSize: 14, lineHeight: 1.65, color: "#334155", marginBottom: 4 }}>{children}</li>;
const DocSubtitle = ({ children }) => <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 8 }}>{children}</div>;
const DocNote = ({ children }) => <div style={{ background: "#F5F3EA", border: "1px solid #E2E0D8", borderRadius: 8, padding: 14, fontSize: 13, color: "#334155", margin: "18px 0", lineHeight: 1.55 }}>{children}</div>;

function TPDocContent({ slug, tpName }) {
  const who = tpName || "Your Company";
  switch (slug) {
    case "handbook":
      return (
        <>
          <DocSubtitle>Getting Started</DocSubtitle>
          <DocH1>Welcome to MedexOVA, {who}.</DocH1>
          <DocP>This handbook is the operator&apos;s reference. Everything you need to run a compliant, high-trust operation on the MedexOVA network lives here. Bookmark it — it&apos;s always the latest version.</DocP>
          <DocH2>Your daily surfaces</DocH2>
          <ul>
            <DocLI><b>Offers</b> — the queue of trips MedexOVA has routed to your company. Accept turns the trip into yours; Decline releases it back to the network. First to accept wins.</DocLI>
            <DocLI><b>Live trips</b> — every trip you own that hasn&apos;t been closed out. Assign a driver here.</DocLI>
            <DocLI><b>Drivers</b> — add and manage your roster. New drivers submit into MedexOVA&apos;s credentialing pipe (MedexOva) automatically.</DocLI>
            <DocLI><b>Earnings & Invoices</b> — Schedule A settlement. Every completed trip lands in your ledger; MedexOVA pays your company on the invoicing cycle.</DocLI>
            <DocLI><b>Documents</b> — you&apos;re here now. Legal, SOP, and Rule Package references.</DocLI>
          </ul>
          <DocH2>The five ceremonies of a compliant trip</DocH2>
          <ol>
            <DocLI>Driver identity + credentials verified before dispatch (automatic).</DocLI>
            <DocLI>Rule Package loaded (which broker, what limits) — automatic.</DocLI>
            <DocLI>Pickup ceremony — driver&apos;s Beacon confirms geofence + selfie at pickup.</DocLI>
            <DocLI>POD ceremony — driver&apos;s Beacon captures signature + geofence at dropoff.</DocLI>
            <DocLI>Receipt sealed — SHA-256 hashes the record; anyone with the hash can verify without seeing PHI.</DocLI>
          </ol>
          <DocNote>
            MedexOVA&apos;s promise: every trip a driver on your roster completes generates an immutable chain-of-custody receipt tied to your company. This is your compliance shield with brokers and regulators.
          </DocNote>
        </>
      );
    case "autonomous":
      return (
        <>
          <DocSubtitle>Autonomous Dispatch</DocSubtitle>
          <DocH1>How Autonomous Dispatch works.</DocH1>
          <DocP>Autonomous Dispatch is an optional operating mode that turns MedexOVA into your dispatch department. Your dispatcher stops handling individual trips and starts handling only exceptions. Small teams can scale to 5× their current fleet without hiring.</DocP>
          <DocH2>Three modes, one control</DocH2>
          <ul>
            <DocLI><b>Manual</b> — you control everything. Review each offer. Pick each driver. This is where new TPs start.</DocLI>
            <DocLI><b>Assisted</b> — the system recommends. You approve or override. Unlocks at Trust Score ≥ 55.</DocLI>
            <DocLI><b>Autonomous</b> — the system executes automatically inside guardrails you set. Only exceptions surface. Unlocks at Trust Score ≥ 70, 90 days on platform, and 200 completed trips (a VA can override with cause).</DocLI>
          </ul>
          <DocH2>What Autonomous does for you</DocH2>
          <ul>
            <DocLI>Accepts eligible offers automatically (based on your guardrails).</DocLI>
            <DocLI>Picks the best driver — factoring credentials, schedule, travel time, vehicle type.</DocLI>
            <DocLI>Fires the Beacon offer to the driver. If the driver declines, auto-picks another.</DocLI>
            <DocLI>Notifies the rider, updates the broker, keeps the timeline stitched — you never touch it.</DocLI>
            <DocLI>Escalates only when a human decision is required.</DocLI>
          </ul>
          <DocH2>Your guardrails</DocH2>
          <DocP>You configure the limits inside which Autonomous can operate. MedexOVA will never accept a trip that violates them. Example guardrails:</DocP>
          <ul>
            <DocLI>Accept trips within 30 miles</DocLI>
            <DocLI>Only wheelchair-capable vehicles</DocLI>
            <DocLI>Fare above $45</DocLI>
            <DocLI>Pickup between 06:00–20:00</DocLI>
            <DocLI>Driver credential score above 80</DocLI>
            <DocLI>Zero schedule conflicts</DocLI>
          </ul>
          <DocH2>The Confidence Score</DocH2>
          <DocP>Every autonomous decision comes with a confidence score (0–100). At or above your threshold (default 90), the system executes silently. Below, it pauses and asks. You can see exactly why every decision was made — no black box.</DocP>
          <DocNote>
            <b>Pricing:</b> Manual is included with your Schedule A relationship. Assisted is $49 / vehicle / month. Autonomous is $99 / vehicle / month <i>or</i> $2 per auto-decision (pay only when it acts). Fleet Command (MedexOVA VA handles your exceptions too) is $149 / vehicle / month.
          </DocNote>
        </>
      );
    case "msa":
      return (
        <>
          <DocSubtitle>Master Service Agreement · draft v1</DocSubtitle>
          <DocH1>MedexOVA / Transportation Provider MSA.</DocH1>
          <DocP><i>This is a first-draft template. MedexOVA legal will provide the final counter-signed version before your first Autonomous or Assisted subscription starts.</i></DocP>
          <DocH2>1. Parties</DocH2>
          <DocP>This Agreement is entered into by Medex Transportation Services LLC (&quot;MedexOVA&quot;) and {who} (&quot;Provider&quot;) as of the date Provider accepts electronically inside this portal.</DocP>
          <DocH2>2. Relationship</DocH2>
          <DocP>Provider is an independent contractor of MedexOVA. MedexOVA operates as a licensed NEMT facilitator and broker. Provider owns its drivers, vehicles, and their compensation. MedexOVA pays Provider per the Schedule A rate sheet attached to this portal.</DocP>
          <DocH2>3. Compliance</DocH2>
          <ul>
            <DocLI>Provider maintains valid commercial auto (≥ $1M CSL), GL (≥ $1M / $2M), and workers&apos; compensation coverage at all times.</DocLI>
            <DocLI>Provider&apos;s drivers must clear MedexOva credentialing before performing trips.</DocLI>
            <DocLI>Provider agrees to MedexOVA&apos;s HIPAA Business Associate terms (published separately).</DocLI>
          </ul>
          <DocH2>4. Term & termination</DocH2>
          <DocP>This Agreement runs month-to-month. Either party may terminate on 30 days written notice. MedexOVA may terminate immediately for cause (credentialing failure, safety incident, or material breach).</DocP>
          <DocH2>5. Payment</DocH2>
          <DocP>MedexOVA pays per completed trip according to Schedule A. Settlement is weekly. Disputes must be raised within 14 days of trip completion via the portal&apos;s dispute form.</DocP>
          <DocH2>6. Confidentiality</DocH2>
          <DocP>Rider PHI, broker rates, and network-wide performance data are confidential. Provider may not disclose them to third parties.</DocP>
          <DocNote>
            <b>Placeholder terms only.</b> MedexOVA will replace this content with the counter-signed MSA. Nothing in this draft creates legal obligations until you sign the final document.
          </DocNote>
        </>
      );
    case "privacy":
      return (
        <>
          <DocSubtitle>Privacy Policy · draft v1</DocSubtitle>
          <DocH1>How MedexOVA handles your data.</DocH1>
          <DocP><i>Draft template. Final counsel-approved version will replace this.</i></DocP>
          <DocH2>Data we collect from your company</DocH2>
          <ul>
            <DocLI>Company identity, insurance, banking (for Schedule A settlement).</DocLI>
            <DocLI>Driver identity, credentials, DL, MVR, background checks — forwarded to MedexOva credentialing repository.</DocLI>
            <DocLI>Vehicle registration + insurance + inspection status.</DocLI>
            <DocLI>Trip performance data (on-time, POD compliance, dispute rate) used to compute your Trust Score.</DocLI>
          </ul>
          <DocH2>How we use it</DocH2>
          <ul>
            <DocLI>Route eligible offers to your company.</DocLI>
            <DocLI>Compute Schedule A settlement.</DocLI>
            <DocLI>Meet broker compliance requirements (Kaizen, MTM, ModivCare, MedexOva).</DocLI>
            <DocLI>Improve the platform for all TPs (aggregated, de-identified).</DocLI>
          </ul>
          <DocH2>What we never do</DocH2>
          <ul>
            <DocLI>Sell your data to third parties.</DocLI>
            <DocLI>Expose rider PHI in any public surface (verified receipts contain only a hash).</DocLI>
            <DocLI>Share your Trust Score with competing TPs.</DocLI>
          </ul>
          <DocH2>Your rights</DocH2>
          <DocP>You may request a copy of every data point MedexOVA holds about your company at any time from info@medexova.com.</DocP>
        </>
      );
    case "use":
      return (
        <>
          <DocSubtitle>Acceptable Use Agreement · draft v1</DocSubtitle>
          <DocH1>Rules for using the MedexOVA platform.</DocH1>
          <DocP><i>Draft template. MedexOVA will replace this with the counter-signed version.</i></DocP>
          <DocH2>You may</DocH2>
          <ul>
            <DocLI>Access this portal to accept offers, manage your roster, and view your settlement.</DocLI>
            <DocLI>Share your dispatcher login with your own team members (audit trail records who did what).</DocLI>
            <DocLI>Export your own data at any time.</DocLI>
          </ul>
          <DocH2>You may not</DocH2>
          <ul>
            <DocLI>Attempt to scrape, reverse-engineer, or automate the portal outside of the published API.</DocLI>
            <DocLI>Share credentials outside your company.</DocLI>
            <DocLI>Impersonate another TP or driver.</DocLI>
            <DocLI>Use the platform for any trip that violates HIPAA, ADA, or applicable transportation regulations.</DocLI>
          </ul>
          <DocH2>Consequences</DocH2>
          <DocP>Violation of this Agreement results in immediate account suspension, forfeiture of pending settlements, and — at MedexOVA&apos;s discretion — permanent removal from the network and referral to brokers as a non-compliant provider.</DocP>
        </>
      );
    case "credentialing":
      return (
        <>
          <DocSubtitle>Credentialing</DocSubtitle>
          <DocH1>Credentialing through MedexOva.</DocH1>
          <DocP>MedexOVA routes every driver and vehicle through the MedexOva credentialing repository. Once cleared, your drivers are accepted by every broker in the network — Kaizen, MTM, ModivCare, and others.</DocP>
          <DocNote>
            If your company was onboarded by a MedexOVA VA, your initial driver and vehicle records were <b>already submitted on your behalf</b>. Check the <b>Drivers</b> and <b>Vehicles</b> tabs to see their current status — no re-submission needed. Use the forms there only to add <b>new</b> drivers or vehicles not yet on file.
          </DocNote>
          <DocH2>Adding new drivers or vehicles</DocH2>
          <ol>
            <DocLI><b>You add</b> — use the Drivers or Vehicles tab to add a new driver/vehicle with the required fields. MedexOVA's VA team completes the credential submission to MedexOva.</DocLI>
            <DocLI><b>MedexOVA reviews</b> — a VA verifies completeness within 1 business day. If anything is missing, your VA will reach out to you directly.</DocLI>
            <DocLI><b>MedexOva clears</b> — status updates here (pending → cleared → active). Cleared drivers can immediately receive trip offers.</DocLI>
          </ol>
          <DocH2>Renewals</DocH2>
          <DocP>Driver licenses, medical cards, and insurance certificates expire. When any credential is within 30 days of expiring, the driver's card in your Drivers tab will show a yellow &ldquo;Renew&rdquo; prompt. Your VA handles the MedexOva update — just respond when they reach out.</DocP>
          <DocNote>
            Every document ever submitted lives in your chain-of-custody card. When a broker asks &ldquo;when did you last verify this driver?&rdquo; you can pull the exact timestamp in one click.
          </DocNote>
        </>
      );
    case "rules":
      return (
        <>
          <DocSubtitle>Rule Package Library</DocSubtitle>
          <DocH1>The rules that govern each service area you serve.</DocH1>
          <DocP>Every service area has a Rule Package — the compliance requirements for the broker(s) that operate in that area. MedexOVA loads the correct package automatically when a trip is offered to you; you can review any package here at any time.</DocP>
          <DocNote>
            The Rule Package view is populated from your active contracts. Once your MSA is signed and MedexOVA has synced your service areas, this section fills in with the exact broker rules that apply to your trips.
          </DocNote>
          <DocH2>What&apos;s inside a Rule Package</DocH2>
          <ul>
            <DocLI>Trip status update SLAs (how fast you must acknowledge, arrive, complete)</DocLI>
            <DocLI>POD requirements (signature, photo, geofence tolerance)</DocLI>
            <DocLI>Cancellation and no-show handling</DocLI>
            <DocLI>Vehicle-type requirements (ambulatory vs wheelchair vs stretcher)</DocLI>
            <DocLI>Driver credential minimums specific to the broker</DocLI>
            <DocLI>Rate sheet reference (linked to your Schedule A)</DocLI>
          </ul>
        </>
      );
    case "faq":
      return (
        <>
          <DocSubtitle>FAQ · Troubleshooting</DocSubtitle>
          <DocH1>Common questions.</DocH1>
          <DocH2>My driver didn&apos;t get the SMS. What do I do?</DocH2>
          <DocP>Open the trip in Live Trips, tap &quot;Resend SMS&quot;. If it still fails, check the driver&apos;s phone number on their card — it must be a mobile line (not a landline or VOIP). If the driver has replied STOP to any MedexOVA message they&apos;re opted out and cannot receive SMS until they text START.</DocP>
          <DocH2>How do I re-submit a rejected credential?</DocH2>
          <DocP>Open the driver in the Drivers tab; the rejection reason is stamped on their card. Update the field and hit Save — MedexOVA forwards the update to MedexOva automatically.</DocP>
          <DocH2>What is Trust Score and how do I improve mine?</DocH2>
          <DocP>Trust Score is a 0–100 measure of your operational health: on-time percentage, POD compliance, credentialing currency, dispute-free rate, and time on platform. It unlocks Autonomous Dispatch and other tiers. To improve: complete trips on time, keep credentials current, submit POD every time.</DocP>
          <DocH2>Can I bring my own dispatch software?</DocH2>
          <DocP>You may — this portal is your dispatch software. If you have an existing system, most functions are also available through MedexOVA&apos;s partner API. Contact info@medexova.com.</DocP>
          <DocH2>How does auto-assign differ from Autonomous Dispatch?</DocH2>
          <DocP>Auto-assign is a simple background worker that picks a driver from your roster at the scheduled dispatch time (60 minutes before pickup). Autonomous Dispatch is the full multi-tier system: it accepts offers on your behalf, picks drivers, validates schedules, and only escalates exceptions. Auto-assign is a piece of Autonomous, not a substitute.</DocP>
        </>
      );
    default:
      return <DocP>Select a document from the left.</DocP>;
  }
}


/**
 * TPSettingsTab · Track 2 · Phase F
 * ─────────────────────────────────
 * Dispatch mode selector (Manual · Assisted · Autonomous) + guardrails form.
 * Reads/writes via GET/PATCH /scoped/settings. Locked modes surface the exact
 * gate the TP hasn't cleared yet (Trust Score / tenure / trip count).
 */
function TPSettingsTab() {
  const [state, setState] = React.useState(null);
  const [prefs, setPrefs] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/scoped/settings`, { headers: authHeaders() });
      setState(r.data);
      setPrefs(r.data.preferences || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load settings");
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const changeMode = async (mode) => {
    setSaving(true);
    try {
      await axios.patch(`${API_BASE}/scoped/settings`, { dispatch_mode: mode }, { headers: authHeaders() });
      toast.success(`Dispatch mode set to ${mode}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update mode");
    } finally { setSaving(false); }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_BASE}/scoped/settings`, { preferences: prefs }, { headers: authHeaders() });
      toast.success("Guardrails saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!state) return <div style={{ padding: 24, color: "#64748B", fontSize: 13 }}>Loading settings…</div>;

  const modeCards = [
    { key: "manual",     title: "Manual",     desc: "You control every offer + driver assignment.",            price: "Included" },
    { key: "assisted",   title: "Assisted",   desc: "System recommends; you approve or override.",             price: "$49 / vehicle / mo" },
    { key: "autonomous", title: "Autonomous", desc: "System executes inside your guardrails. Only exceptions surface.", price: "$99 / vehicle / mo · or $2 per decision" },
  ];

  return (
    <div data-testid="tp-settings-tab">
      <div style={{ fontSize: 22, fontWeight: 800, color: "#0F1F17", marginBottom: 6 }}>Dispatch mode & guardrails</div>
      <div style={{ fontSize: 13, color: "#64748B", marginBottom: 24 }}>
        Trust Score: <b style={{ color: "#0F1F17" }}>{Number(state.trust_score ?? 0).toFixed(1)}</b>
        {" · "}Completed trips: <b style={{ color: "#0F1F17" }}>{state.completed_trip_count}</b>
      </div>

      {/* Mode cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginBottom: 32 }}>
        {modeCards.map(m => {
          const info = state.unlocked_modes?.[m.key] || { unlocked: false, reason: "" };
          const active = state.dispatch_mode === m.key;
          const disabled = !info.unlocked;
          return (
            <button key={m.key} disabled={disabled || saving} onClick={() => changeMode(m.key)}
              data-testid={`tp-mode-${m.key}`}
              style={{
                textAlign: "left",
                background: active ? "#2B4C3B" : "#fff",
                color:      active ? "#fff"    : "#0F1F17",
                border:     active ? "2px solid #2B4C3B" : "1px solid #E2E0D8",
                borderRadius: 12, padding: 18,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                position: "relative",
              }}>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: active ? "#94D4A3" : "#94A3B8", fontWeight: 800, marginBottom: 6 }}>
                {active ? "✓ Current mode" : (disabled ? "Locked" : "Available")}
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 6 }}>{m.title}</div>
              <div style={{ fontSize: 12, color: active ? "rgba(255,255,255,0.75)" : "#64748B", lineHeight: 1.55, marginBottom: 10 }}>
                {m.desc}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: active ? "#F5D48C" : "#2B4C3B" }}>
                {m.price}
              </div>
              {disabled && info.reason && (
                <div style={{ marginTop: 10, fontSize: 11, color: "#B45309", lineHeight: 1.45 }}>
                  {info.reason}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Guardrails */}
      <div style={{ fontSize: 15, fontWeight: 800, color: "#0F1F17", marginBottom: 4 }}>Autonomous guardrails</div>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 18 }}>
        The limits inside which Autonomous will act on your behalf. Even in Manual mode, saving these means the Assisted recommender will use them.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginBottom: 20 }}>
        <PrefField label="Accept distance ≤ (miles)" value={prefs.accept_distance_max_mi ?? ""} onChange={(v) => setPrefs({ ...prefs, accept_distance_max_mi: Number(v) })} testId="pref-distance"/>
        <PrefField label="Minimum fare (USD)"        value={prefs.accept_min_fare_usd ?? ""}   onChange={(v) => setPrefs({ ...prefs, accept_min_fare_usd:   Number(v) })} testId="pref-fare"/>
        <PrefField label="Pickup window start (hr)"  value={prefs.pickup_window_start_hr ?? ""} onChange={(v) => setPrefs({ ...prefs, pickup_window_start_hr: Number(v) })} testId="pref-window-start"/>
        <PrefField label="Pickup window end (hr)"    value={prefs.pickup_window_end_hr ?? ""}   onChange={(v) => setPrefs({ ...prefs, pickup_window_end_hr:  Number(v) })} testId="pref-window-end"/>
        <PrefField label="Credential score minimum"  value={prefs.credential_score_min ?? ""}   onChange={(v) => setPrefs({ ...prefs, credential_score_min:  Number(v) })} testId="pref-cred-min"/>
        <PrefField label="Confidence threshold (0-100)" value={prefs.confidence_threshold ?? ""} onChange={(v) => setPrefs({ ...prefs, confidence_threshold: Number(v) })} testId="pref-confidence"/>
      </div>
      <button onClick={savePrefs} disabled={saving} data-testid="tp-save-prefs"
        style={{ background: "#2B4C3B", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
        {saving ? "Saving…" : "Save guardrails"}
      </button>

      <TPBillingPanel />
      <TPPayoutsPanel />
      <TPAutonomousReport />
    </div>
  );
}

function PrefField({ label, value, onChange, testId }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        style={{ width: "100%", padding: "8px 12px", border: "1px solid #E2E0D8", borderRadius: 6, fontSize: 14 }}/>
    </label>
  );
}


/**
 * TPBillingPanel · Track 2 · Phase L
 * ────────────────────────────────────
 * Stripe subscription card. Assisted ($49/mo) or Autonomous ($99/mo + $2/decision).
 * Reads GET /scoped/billing, launches Checkout via POST /scoped/billing/checkout,
 * opens the Stripe Customer Portal, or cancels the subscription immediately.
 */
function TPBillingPanel() {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/scoped/billing`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      // Billing not wired → hide panel silently.
      setData({ error: e?.response?.data?.detail || "unavailable" });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // Auto-refresh after Stripe redirects back with ?billing=success.
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("billing") === "success") {
      toast.success("Subscription active — refreshing…");
      setTimeout(load, 1500);  // give webhook a moment to fire
      // Clear the query params so refresh doesn't retrigger
      window.history.replaceState({}, "", window.location.pathname);
    } else if (p.get("billing") === "cancel") {
      toast.info("Checkout canceled.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  if (!data || data.error) return null;

  const subscribe = async (plan) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_BASE}/scoped/billing/checkout`,
        { plan, origin_url: window.location.origin },
        { headers: authHeaders() },
      );
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/scoped/billing/portal`, {}, { headers: authHeaders() });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription now? Your dispatch mode will drop to Manual immediately.")) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/scoped/billing/cancel`, {}, { headers: authHeaders() });
      toast.success("Subscription canceled. Dispatch mode reset to Manual.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally { setBusy(false); }
  };

  const sub = data.subscription || {};
  const active = sub.status === "active" || sub.status === "trialing";
  const planLabel = sub.plan_mode
    ? `${sub.plan_mode.charAt(0).toUpperCase()}${sub.plan_mode.slice(1)}`
    : "None";
  const badge = active ? { bg: "#DCFCE7", fg: "#166534", label: `Active · ${planLabel}` }
                       : sub.status === "canceled" ? { bg: "#FEE2E2", fg: "#991B1B", label: "Canceled" }
                       : { bg: "#F1F5F9", fg: "#334155", label: "No subscription" };

  return (
    <div data-testid="tp-billing-panel" style={{ marginTop: 40, padding: 24, background: "#FAFAF6", border: "1px solid #E2E0D8", borderRadius: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#0F1F17" }}>Dispatch subscription</div>
        <span data-testid="tp-billing-status" style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 999, background: badge.bg, color: badge.fg }}>
          {badge.label}
        </span>
      </div>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 18 }}>
        Assisted and Autonomous modes require an active subscription.
        {sub.current_period_end && active && (
          <> Renews {new Date(sub.current_period_end).toLocaleDateString()}.</>
        )}
      </div>

      {!active && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
          {(data.plans || []).map((p) => (
            <div key={p.key} style={{ padding: 18, background: "#fff", border: "1px solid #E2E0D8", borderRadius: 10 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#94A3B8", fontWeight: 800, marginBottom: 4 }}>Plan</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#0F1F17", marginBottom: 4 }}>{p.name}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#2B4C3B", marginBottom: 8 }}>{p.price_display}</div>
              <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.55, marginBottom: 14 }}>{p.description}</div>
              <button data-testid={`tp-subscribe-${p.key}`} disabled={busy} onClick={() => subscribe(p.key)}
                style={{ width: "100%", background: "#2B4C3B", color: "#fff", border: "none", padding: "10px 16px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
                {busy ? "…" : `Subscribe · ${p.name}`}
              </button>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button data-testid="tp-billing-portal" disabled={busy} onClick={openPortal}
            style={{ background: "#2B4C3B", color: "#fff", border: "none", padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
            Manage billing & invoices
          </button>
          <button data-testid="tp-billing-cancel" disabled={busy} onClick={cancel}
            style={{ background: "#fff", color: "#991B1B", border: "1px solid #FCA5A5", padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
            Cancel subscription
          </button>
        </div>
      )}
    </div>
  );
}



/**
 * TPPayoutsPanel — Stripe Connect Express (company). Onboard once to receive
 * real Stripe transfers whenever an admin settles a completed-trip invoice.
 */
function TPPayoutsPanel() {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/scoped/payouts/status`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) {
      setStatus({ error: e?.response?.data?.detail || "unavailable" });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("payouts") === "done") {
      toast.success("Payout account updated — refreshing…");
      setTimeout(load, 1200);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  if (!status || status.error) return null;

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/scoped/payouts/onboarding-link`, {}, { headers: authHeaders() });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start onboarding");
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/scoped/payouts/dashboard-link`, {}, { headers: authHeaders() });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open payout dashboard");
      setBusy(false);
    }
  };

  const badge = status.onboarded
    ? { bg: "#DCFCE7", fg: "#166534", label: "Onboarded" }
    : { bg: "#FEF3C7", fg: "#92400E", label: "Not onboarded" };

  return (
    <div data-testid="tp-payouts-panel" style={{ marginTop: 24, padding: 24, background: "#FAFAF6", border: "1px solid #E2E0D8", borderRadius: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#0F1F17" }}>Payout account</div>
        <span data-testid="tp-payouts-status" style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 999, background: badge.bg, color: badge.fg }}>
          {badge.label}
        </span>
      </div>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 18 }}>
        {status.message || "Set up once to receive Stripe transfers directly whenever MedexOVA settles an invoice for completed trips."}
      </div>
      {!status.onboarded ? (
        <button data-testid="tp-payouts-onboard-btn" disabled={busy} onClick={startOnboarding}
          style={{ background: "#2B4C3B", color: "#fff", border: "none", padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "…" : "Set up payouts →"}
        </button>
      ) : (
        <button data-testid="tp-payouts-dashboard-btn" disabled={busy} onClick={openDashboard}
          style={{ background: "#fff", color: "#2B4C3B", border: "1px solid #2B4C3B", padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
          Manage payouts
        </button>
      )}
    </div>
  );
}



/**
 * TPExceptionsTab · Track 2 · Phase H
 * ────────────────────────────────────
 * The only screen an Autonomous-mode dispatcher really needs. Shows every
 * trip flagged by the Autonomous runner (no eligible driver · low confidence
 * · driver declined · guardrail miss · credential expired). Resolutions are
 * stamped with a reason code that feeds the guardrail tuning loop.
 */
const EXCEPTION_LABELS = {
  NO_ELIGIBLE_DRIVER:        "No eligible driver",
  DRIVER_DECLINED:           "Driver declined",
  LATE_PICKUP:               "Late pickup",
  CREDENTIAL_EXPIRED:        "Credential expired",
  VEHICLE_UNAVAILABLE:       "Vehicle unavailable",
  RULE_PACKAGE_VIOLATION:    "Rule package violation",
  BROKER_UPDATED:            "Broker updated trip",
  RIDER_CANCELLED:           "Rider cancelled",
  AUTONOMOUS_ERROR:          "Autonomous runner error",
};

const RESOLUTION_REASONS = [
  ["too_far",              "Too far — reduce accept radius"],
  ["driver_unavailable",   "Driver unavailable — pick another"],
  ["revenue_too_low",      "Revenue too low — raise minimum fare"],
  ["rider_specific",       "Rider-specific request"],
  ["credential_issue",     "Credential issue — flagged for renewal"],
  ["operational_override", "Operational override — one-off exception"],
];

function TPExceptionsTab() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [pickTrip, setPickTrip] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/scoped/exceptions`, { headers: authHeaders() });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load exceptions");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  if (loading) return <div style={{ padding: 24, color: "#64748B", fontSize: 13 }}>Loading exceptions…</div>;

  return (
    <div data-testid="tp-exceptions-tab">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#B45309", fontWeight: 700 }}>
            Needs attention
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0F1F17" }}>
            {rows.length} exception{rows.length === 1 ? "" : "s"}
          </div>
        </div>
        {rows.length === 0 && (
          <div style={{ fontSize: 12, color: "#2B4C3B", fontWeight: 700, background: "#E7F5EC", padding: "6px 12px", borderRadius: 6 }}>
            ✓ All clear
          </div>
        )}
      </div>

      {rows.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", background: "#F5F3EA", border: "1px dashed #E2E0D8", borderRadius: 10 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✨</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0F1F17", marginBottom: 4 }}>Nothing needs your attention</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>
            When Autonomous Dispatch runs into something it can&apos;t decide, it will surface here.
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 10 }}>
        {rows.map(t => (
          <div key={t.trip_id} style={{ background: "#fff", border: "1px solid #F5D48C", borderLeft: "4px solid #B45309", borderRadius: 8, padding: 14 }}
               data-testid={`tp-exception-card-${t.trip_id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontFamily: "ui-monospace,Menlo,monospace", color: "#64748B", marginBottom: 4 }}>
                  {(t.trip_id || "").slice(0, 8).toUpperCase()} · {t.status}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1F17" }}>{t.rider?.name || "Rider"}</div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                  {t.pickup?.address || "?"} → {t.dropoff?.address || "?"}
                </div>
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(t.exceptions || []).map((e, i) => (
                    <span key={i} style={{ fontSize: 11, background: "#FEF3C7", color: "#B45309", padding: "3px 8px", borderRadius: 999, fontWeight: 700 }}>
                      {EXCEPTION_LABELS[e.code] || e.code}
                    </span>
                  ))}
                </div>
                {(t.autonomous_suggestions || []).length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: "#64748B" }}>
                    💡 Suggested: driver <b>{t.autonomous_suggestions.at(-1)?.driver_id?.slice(0, 8) || "?"}</b> · confidence {t.autonomous_suggestions.at(-1)?.confidence ?? "?"}
                  </div>
                )}
              </div>
              <button onClick={() => setPickTrip(t)} data-testid={`tp-exception-resolve-${t.trip_id}`}
                style={{ background: "#2B4C3B", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 800, cursor: "pointer", alignSelf: "flex-start" }}>
                Resolve
              </button>
            </div>
          </div>
        ))}
      </div>

      {pickTrip && <ResolveExceptionModal trip={pickTrip} onClose={() => setPickTrip(null)} onResolved={() => { setPickTrip(null); load(); }}/>}
    </div>
  );
}

function ResolveExceptionModal({ trip, onClose, onResolved }) {
  const [resolution, setResolution] = React.useState("reassign");
  const [reason, setReason]         = React.useState("");
  const [note, setNote]             = React.useState("");
  const [busy, setBusy]             = React.useState(false);

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason so we can tune your guardrails"); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/scoped/exceptions/${trip.trip_id}/resolve`, {
        resolution, reason_code: reason, note: note || null,
      }, { headers: authHeaders() });
      toast.success("Exception resolved");
      onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="resolve-exception-dialog">
        <div className="text-lg font-bold mb-1">Resolve exception</div>
        <div className="text-xs text-slate-500 mb-4">Trip {(trip.trip_id || "").slice(0, 8).toUpperCase()} · {trip.rider?.name}</div>

        <label className="block mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Resolution</div>
          <select value={resolution} onChange={(e) => setResolution(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="resolve-action">
            <option value="reassign">Reassign to another driver</option>
            <option value="escalate_va">Escalate to MedexOVA VA</option>
            <option value="dismiss">Dismiss (no action)</option>
          </select>
        </label>

        <label className="block mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Reason code</div>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="resolve-reason">
            <option value="">— pick one —</option>
            {RESOLUTION_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div className="text-[11px] text-slate-500 mt-1">Every reason feeds guardrail tuning. Pick honestly.</div>
        </label>

        <label className="block mb-6">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Note (optional)</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="resolve-note"/>
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-slate-500 px-3 py-2" data-testid="resolve-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || !reason}
            className={`text-sm font-semibold px-5 py-2 rounded-md ${!reason ? "bg-slate-200 text-slate-400" : "bg-brandblue-ink text-white hover:bg-brandblue-deep"}`}
            data-testid="resolve-submit">
            {busy ? "Resolving…" : "Resolve"}
          </button>
        </div>
      </div>
    </div>
  );
}



/**
 * TPAutonomousReport · Track 2 · Phases J + K
 * ────────────────────────────────────────────
 * Monthly Confidence Report — reliability score, hours saved, override rate,
 * top reason codes. Autonomous Verified badge surfaces when reliability ≥ 85
 * over 100+ trips at ≤5% override rate.
 */
function TPAutonomousReport() {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/scoped/autonomous-report?months=1`, { headers: authHeaders() });
        setData(r.data);
      } catch (e) {
        // Silently fail — this is an optional add-on card
      }
    })();
  }, []);

  if (!data) return null;

  return (
    <div style={{ marginTop: 40, borderTop: "1px solid #E9EAE4", paddingTop: 32 }} data-testid="tp-autonomous-report">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700 }}>Autonomous Report · last 30 days</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0F1F17" }}>Your dispatch impact</div>
        </div>
        {data.autonomous_verified && (
          <div style={{ background: "linear-gradient(135deg,#F5D48C,#F59E0B)", color: "#0F1F17", padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}
               data-testid="tp-autonomous-verified">
            ★ Autonomous Verified
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 24 }}>
        <ReportTile label="Reliability Score"   value={data.reliability_score}      unit="/ 100" tone="hero" testId="report-reliability"/>
        <ReportTile label="Trips auto-completed" value={data.trips_auto_completed}  unit=""      testId="report-completed"/>
        <ReportTile label="Override rate"       value={data.override_rate_pct}     unit="%"     testId="report-override"/>
        <ReportTile label="Avg confidence"      value={data.avg_confidence}        unit="/ 100" testId="report-confidence"/>
        <ReportTile label="Hours saved"         value={data.hours_saved_estimate}  unit="hr"    testId="report-hours"/>
        <ReportTile label="Est. dollars saved"  value={`$${data.dollars_saved_estimate}`} unit="" testId="report-dollars"/>
      </div>

      {data.top_reason_codes?.length > 0 && (
        <div style={{ background: "#F5F3EA", border: "1px solid #E2E0D8", borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 8 }}>
            Guardrail tuning suggestions
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {data.top_reason_codes.slice(0, 3).map((r, i) => (
              <div key={i} style={{ fontSize: 13, color: "#334155" }}>
                <b>{r.reason_code}</b> · overridden {r.count}x — consider tuning the matching guardrail
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportTile({ label, value, unit, tone, testId }) {
  const isHero = tone === "hero";
  return (
    <div style={{
      background: isHero ? "#2B4C3B" : "#fff",
      color:      isHero ? "#fff"    : "#0F1F17",
      border:     isHero ? "none"    : "1px solid #E2E0D8",
      borderRadius: 10, padding: 14,
    }} data-testid={testId}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: isHero ? "rgba(255,255,255,0.7)" : "#64748B", fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1 }}>
        {value ?? "—"}
        <span style={{ fontSize: 12, fontWeight: 700, marginLeft: 4, color: isHero ? "rgba(255,255,255,0.7)" : "#64748B" }}>{unit}</span>
      </div>
    </div>
  );
}



/**
 * TPVehiclesTab · Track 3 · Phase P
 * ──────────────────────────────────
 * List + add vehicles.  Ease-of-use focus: single "+ Add vehicle" button,
 * form drops down inline; credential status pill on every row is the
 * dominant visual (green cleared · amber pending · red expired).
 */
const VEHICLE_STATUS_PILL = {
  not_submitted:    { bg: "#F5F3EA", fg: "#64748B", label: "Awaiting review" },
  pending:          { bg: "#FEF3C7", fg: "#B45309", label: "MedexOva reviewing" },
  cleared:          { bg: "#E7F5EC", fg: "#2B4C3B", label: "✓ Cleared" },
  rejected:         { bg: "#FEE2E2", fg: "#B91C1C", label: "Rejected · fix needed" },
  expired:          { bg: "#FEE2E2", fg: "#B91C1C", label: "Expired" },
};

function CredentialPill({ status, testId }) {
  const s = VEHICLE_STATUS_PILL[status] || VEHICLE_STATUS_PILL.not_submitted;
  return (
    <span data-testid={testId}
      style={{ fontSize: 11, background: s.bg, color: s.fg, padding: "3px 10px", borderRadius: 999, fontWeight: 700, letterSpacing: "0.02em" }}>
      {s.label}
    </span>
  );
}

function TPVehiclesTab({ tpId }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({ vin: "", plate: "", make: "", model: "", year: "", vehicle_type: "sedan", capacity: 4, wheelchair_lift: false });
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/scoped/vehicles`, { headers: authHeaders() });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load vehicles");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.vin || !form.plate) { toast.error("VIN and plate are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_BASE.replace("/tpt","/tpt")}/providers/${tpId}/vehicles`, {
        ...form,
        year: form.year ? Number(form.year) : null,
        capacity: form.capacity ? Number(form.capacity) : null,
      }, { headers: authHeaders() });
      toast.success("Vehicle submitted — awaiting MedexOVA review");
      setAdding(false);
      setForm({ vin: "", plate: "", make: "", model: "", year: "", vehicle_type: "sedan", capacity: 4, wheelchair_lift: false });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 24, color: "#64748B", fontSize: 13 }}>Loading vehicles…</div>;

  return (
    <div data-testid="tp-vehicles-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700 }}>Fleet</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0F1F17" }}>{rows.length} vehicle{rows.length === 1 ? "" : "s"}</div>
        </div>
        <button onClick={() => setAdding(!adding)} data-testid="tp-add-vehicle-toggle"
          style={{ background: adding ? "#64748B" : "#2B4C3B", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
          {adding ? "Cancel" : "+ Add vehicle"}
        </button>
      </div>

      {adding && (
        <div style={{ background: "#F5F3EA", border: "1px solid #E2E0D8", borderRadius: 10, padding: 18, marginBottom: 24 }} data-testid="tp-add-vehicle-form">
          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 12 }}>
            Add a vehicle · 3-step process
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
            <VF label="VIN *"   v={form.vin}       onChange={(v) => setForm({ ...form, vin: v.toUpperCase() })}   testId="vf-vin"/>
            <VF label="Plate *" v={form.plate}     onChange={(v) => setForm({ ...form, plate: v.toUpperCase() })} testId="vf-plate"/>
            <VF label="Make"    v={form.make}      onChange={(v) => setForm({ ...form, make: v })}    testId="vf-make"/>
            <VF label="Model"   v={form.model}     onChange={(v) => setForm({ ...form, model: v })}   testId="vf-model"/>
            <VF label="Year"    v={form.year}      onChange={(v) => setForm({ ...form, year: v })}    testId="vf-year" type="number"/>
            <VF label="Capacity" v={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} testId="vf-capacity" type="number"/>
            <label style={{ display: "block" }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 4 }}>Vehicle type</div>
              <select value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} data-testid="vf-type"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #E2E0D8", borderRadius: 6, fontSize: 14, background: "#fff" }}>
                <option value="sedan">Sedan</option>
                <option value="van">Van</option>
                <option value="wheelchair">Wheelchair</option>
                <option value="stretcher">Stretcher</option>
              </select>
            </label>
            <label style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
              <input type="checkbox" checked={form.wheelchair_lift} onChange={(e) => setForm({ ...form, wheelchair_lift: e.target.checked })} data-testid="vf-lift"/>
              <span style={{ fontSize: 13, color: "#0F1F17" }}>Wheelchair lift installed</span>
            </label>
          </div>
          <div style={{ fontSize: 11, color: "#64748B", marginBottom: 12, lineHeight: 1.55 }}>
            <b style={{ color: "#2B4C3B" }}>Step 1</b>: You submit here · <b style={{ color: "#2B4C3B" }}>Step 2</b>: MedexOVA reviews within 1 business day · <b style={{ color: "#2B4C3B" }}>Step 3</b>: MedexOVA credentials → vehicle goes live
          </div>
          <button onClick={submit} disabled={saving} data-testid="vf-submit"
            style={{ background: "#2B4C3B", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Submitting…" : "Submit for credentialing"}
          </button>
        </div>
      )}

      {rows.length === 0 && !adding && (
        <div style={{ padding: 32, textAlign: "center", background: "#F5F3EA", border: "1px dashed #E2E0D8", borderRadius: 10, color: "#64748B", fontSize: 13 }}>
          No vehicles yet. Add one to unlock trips that require it.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
        {rows.map(v => (
          <div key={v.vehicle_id} style={{ background: "#fff", border: "1px solid #E2E0D8", borderRadius: 10, padding: 14 }} data-testid={`tp-vehicle-card-${v.vehicle_id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#0F1F17" }}>{v.year || ""} {v.make || ""} {v.model || v.vehicle_type}</div>
              <CredentialPill status={v.medexova_credentialing_status || "not_submitted"} testId={`vehicle-status-${v.vehicle_id}`}/>
            </div>
            <div style={{ fontSize: 11, fontFamily: "ui-monospace,Menlo,monospace", color: "#64748B", marginBottom: 4 }}>
              VIN {v.vin} · Plate {v.plate}
            </div>
            <div style={{ fontSize: 12, color: "#334155" }}>
              {v.vehicle_type}{v.wheelchair_lift ? " · wheelchair lift" : ""}{v.capacity ? ` · ${v.capacity} seats` : ""}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 24, background: "#F5F3EA", border: "1px dashed #D0CDC0", borderRadius: 10, padding: 18, opacity: 0.85 }} data-testid="tp-camera-placeholder-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#0F1F17" }}>In-vehicle camera integration</div>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 800, color: "#B45309", background: "#FEF3C7", padding: "3px 10px", borderRadius: 999 }}>Coming soon</span>
        </div>
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 6 }}>Live + recorded footage from dashcams/interior cameras will attach automatically to each trip's chain-of-custody receipt. Not yet available — this is a placeholder for the PAID camera add-on.</div>
      </div>
    </div>
  );
}

function VF({ label, v, onChange, testId, type }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <input type={type || "text"} value={v} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        style={{ width: "100%", padding: "8px 12px", border: "1px solid #E2E0D8", borderRadius: 6, fontSize: 14 }}/>
    </label>
  );
}

function TPBrandingTab({ tpId, branding, onSaved }) {
  const [color, setColor] = useState(branding?.primary_color || "#2B4C3B");
  const [message, setMessage] = useState(branding?.welcome_message || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());

  const hasLogo = !!branding?.logo_storage_path;

  const saveText = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/providers/${tpId}/branding`, { primary_color: color, welcome_message: message }, { headers: authHeaders() });
      toast.success("Branding saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API_BASE}/providers/${tpId}/branding/logo`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      setLogoBust(Date.now());
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ maxWidth: 480, background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, padding: 24 }} data-testid="tp-branding-tab">
      <div style={{ fontSize: 16, fontWeight: 800, color: "#0F1F17", marginBottom: 4 }}>White-label branding</div>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 20 }}>Your logo, color, and welcome message replace MedexOVA's default look in this portal — a PAID-tier perk.</div>

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 6 }}>Logo</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {hasLogo ? (
            <img key={logoBust} src={`${API_BASE}/providers/${tpId}/branding-logo?t=${logoBust}`} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "contain", background: "#F1F5F9", border: "1px solid #E2E0D8" }} data-testid="tp-branding-logo-preview"/>
          ) : (
            <div style={{ width: 56, height: 56, borderRadius: 10, background: "#F1F5F9", border: "1px dashed #CBD5E1" }}/>
          )}
          <label style={{ fontSize: 12, fontWeight: 700, color: "#2B4C3B", cursor: "pointer", border: "1px solid #2B4C3B", borderRadius: 8, padding: "8px 14px" }}>
            {uploading ? "Uploading…" : "Upload logo"}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="tp-branding-logo-input" style={{ display: "none" }}/>
          </label>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 6 }}>Primary color</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="tp-branding-color-input" style={{ width: 44, height: 34, border: "1px solid #E2E0D8", borderRadius: 6, padding: 0 }}/>
          <span style={{ fontSize: 13, fontFamily: "ui-monospace,monospace", color: "#64748B" }}>{color}</span>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 6 }}>Welcome message</div>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={280} rows={3} placeholder="e.g. Welcome to the Sunrise Medical Transport dispatch portal"
          data-testid="tp-branding-message-input"
          style={{ width: "100%", padding: "10px 12px", border: "1px solid #E2E0D8", borderRadius: 8, fontSize: 13, resize: "vertical" }}/>
      </div>

      <button onClick={saveText} disabled={saving} data-testid="tp-branding-save"
        style={{ background: "#2B4C3B", color: "#fff", fontWeight: 700, fontSize: 13, padding: "10px 18px", borderRadius: 8, border: "none", cursor: "pointer" }}>
        {saving ? "Saving…" : "Save branding"}
      </button>
    </div>
  );
}

/* -------- PAID-tier upsell + coming-soon panels -------- */
function TPUpsellCard({ label }) {
  return (
    <div style={{ padding: 40, textAlign: "center", background: "linear-gradient(135deg,#0B1220 0%,#142433 100%)", borderRadius: 16, color: "#E2E8F0" }} data-testid="tp-upsell-card">
      <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "#F59E0B", fontWeight: 800, marginBottom: 10 }}>PAID feature</div>
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>{label} isn&apos;t enabled on your plan</div>
      <div style={{ fontSize: 13, color: "#94A3B8", maxWidth: 420, margin: "0 auto" }}>Ask MedexOVA to turn this on from your Portal tier settings — it&apos;s billed as a simple monthly add-on alongside anything else you enable.</div>
    </div>
  );
}

function TPComingSoonTab({ testId, title, blurb }) {
  return (
    <div data-testid={testId} style={{ padding: 32, background: "#fff", border: "1px dashed #E2E0D8", borderRadius: 12, textAlign: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#0F1F17", marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: "#64748B", maxWidth: 420, margin: "0 auto" }}>{blurb}</div>
      <div style={{ marginTop: 14, display: "inline-block", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 800, color: "#B45309", background: "#FEF3C7", padding: "4px 10px", borderRadius: 999 }}>Coming soon</div>
    </div>
  );
}

function TPPayrollTab() {
  return <TPComingSoonTab testId="tp-payroll-tab" title="Driver Payroll" blurb="Automatic payroll runs from completed-trip earnings, tax withholding, and direct-deposit exports. MedexOVA is rolling this out to PAID providers in phases." />;
}

function TPReconciliationTab() {
  return <TPComingSoonTab testId="tp-reconciliation-tab" title="Broker/Facility Reconciliation" blurb="Line-by-line matching between your invoices and broker/facility remittances, with automatic dispute flags on mismatches." />;
}

function TPInsuranceTab() {
  return <TPComingSoonTab testId="tp-insurance-tab" title="Insurance Tracking" blurb="Auto, GL, and workers' comp policy expirations tracked with 30/60/90-day renewal alerts across your whole fleet." />;
}

function TPKPITab() {
  const [rows, setRows] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/driver-leaderboard`, { headers: authHeaders() })
      .then((r) => { if (!cancelled) setRows(r.data.drivers || []); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load leaderboard"); });
    return () => { cancelled = true; };
  }, []);

  const pct = (v) => (v == null ? "—" : `${v}%`);
  return (
    <div data-testid="tp-kpi-tab" style={{ background: "#fff", border: "1px solid #E2E0D8", borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#0F1F17", marginBottom: 4 }}>Driver scorecard</div>
      <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>Trip count, on-time %, completion %, and acceptance % across your fleet — ranked by trips completed.</div>
      {err && <div style={{ fontSize: 12, color: "#B91C1C" }}>{err}</div>}
      {!err && !rows && <div style={{ fontSize: 13, color: "#64748B" }}>Loading…</div>}
      {rows && rows.length === 0 && <div style={{ fontSize: 13, color: "#64748B" }} data-testid="tp-kpi-empty">No drivers yet — add one from the Drivers tab.</div>}
      {rows && rows.length > 0 && (
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }} data-testid="tp-kpi-leaderboard">
          <thead>
            <tr style={{ background: "#F4F1EA", color: "#475569" }}>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>Driver</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>Trips</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>Rating</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>On-time</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>Completion</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>Acceptance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d, i) => (
              <tr key={d.id} style={{ borderTop: "1px solid #E2E0D8" }} data-testid={`tp-kpi-row-${d.id}`}>
                <td style={{ padding: "8px 10px", fontWeight: 600, color: "#0F1F17" }}>{i === 0 && "🏆 "}{d.name}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{d.trip_count}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{Number(d.rating || 5).toFixed(1)}★</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{pct(d.on_time_pct)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{pct(d.completion_pct)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{pct(d.acceptance_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

